import React, { useState, useEffect } from 'react'
import { FaLink } from 'react-icons/fa'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

// Guest Links defaults to Not Available for every invitation.
// Add future invitation IDs here only after their guest-link feature is configured.
const GUEST_LINK_ENABLED_INVITATIONS = new Set([])

export default function GuestLinkGenerator({
  defaultClientId = '',
  defaultPath = '/',
  purpose,
  onUnavailableClose,
}) {
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [path, setPath] = useState(defaultPath || '/')
  const [generated, setGenerated] = useState('')
  const [status, setStatus] = useState('')
  const [resolvedFromCode, setResolvedFromCode] = useState(null)
  const [availability, setAvailability] = useState('checking')

  // Resolve the invitation from the client access code before deciding whether
  // its personalized guest-link feature is enabled.
  useEffect(() => {
    const code = (localStorage.getItem('seat_checker_access_code') || '').trim()
    if (!code) {
      setAvailability('unavailable')
      return undefined
    }

    const endpoint = `${API_BASE_URL}/rsvp/public-summary?code=${encodeURIComponent(code)}`
    let cancelled = false

    ;(async () => {
      try {
        const res = await fetch(endpoint)
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
          if (!cancelled) setAvailability('unavailable')
          return
        }

        const invitationId = String(json.invitationId || json.id || '').trim()
        const maybeClientId = json.clientId || json.client_id || json.organizerId || json.organizer_id || ''

        if (invitationId && !cancelled) {
          const autoPath = `/invitations/custom/${invitationId}`
          setPath(autoPath)
          setResolvedFromCode({ code, invitationId, clientId: maybeClientId || null })
          setAvailability(
            GUEST_LINK_ENABLED_INVITATIONS.has(invitationId.toLowerCase())
              ? 'available'
              : 'unavailable'
          )
        } else if (maybeClientId && !cancelled) {
          setResolvedFromCode({ code, invitationId: null, clientId: maybeClientId })
          setAvailability('unavailable')
        } else if (!cancelled) {
          setAvailability('unavailable')
        }
      } catch (err) {
        if (!cancelled) setAvailability('unavailable')
      }
    })()

    return () => { cancelled = true }
  }, [])

  function buildUrl() {
    const base = window.location.origin
    const cleanPath = path.startsWith('/') ? path : `/${path}`
    const params = new URLSearchParams()
    if (name) params.set('guestName', name)
    if (role) params.set('guestRole', role)
    return `${base}${cleanPath}${params.toString() ? `?${params.toString()}` : ''}`
  }

  async function handleGenerate(e) {
    e.preventDefault()
    const url = buildUrl()
    setGenerated(url)
    setStatus('Registering...')
    try {
      const accessCode = (localStorage.getItem('seat_checker_access_code') || '').trim() || null
      const payload = {
        clientId: (resolvedFromCode && resolvedFromCode.clientId) || defaultClientId || null,
        accessCode,
        url,
        name,
        role,
      }
      const res = await fetch(`${API_BASE_URL}/guest-links/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      const data = await res.json()
      if (res.ok) {
        setStatus(`Registered (total: ${data.count})`)
      } else {
        setStatus(data.error || 'Registration failed')
      }
    } catch (err) {
      setStatus(err.message)
    }
  }

  function copyToClipboard() {
    if (!generated) return
    navigator.clipboard.writeText(generated)
  }

  if (availability === 'checking') {
    return (
      <div className="printable-loading-panel guest-link-loading-panel" role="status">
        <div className="printable-icon guest-link-feature-icon" aria-hidden="true">
          <FaLink />
        </div>
        <p className="printable-status">Checking Guest Link availability...</p>
      </div>
    )
  }

  if (availability === 'unavailable') {
    return <NoGuestLinkState onClose={onUnavailableClose} purpose={purpose} />
  }

  return (
    <div className="guest-link-generator">
      <form onSubmit={handleGenerate} className="gl-form">
        {(!defaultPath) && (
          <div className="field">
            <label className="field-label">Invitation path</label>
            <input className="field-input" value={path} onChange={e => setPath(e.target.value)} placeholder="/wedding/..." />
          </div>
        )}

        {resolvedFromCode && (
          <div style={{ marginBottom: 8, color: '#555', fontSize: 13 }}>
            Invitation auto-resolved from access code: <strong>{resolvedFromCode.invitationId}</strong>
          </div>
        )}

        <div className="field-row">
          <div className="field">
            <label className="field-label">Guest name</label>
            <input className="field-input" value={name} onChange={e => setName(e.target.value)} placeholder="Juan" required />
          </div>
          <div className="field">
            <label className="field-label">Guest role</label>
            <input className="field-input" value={role} onChange={e => setRole(e.target.value)} placeholder="Bridesmaid" />
          </div>
        </div>

        <div className="form-actions">
          <button className="btn primary" type="submit">Generate & Register</button>
        </div>
      </form>

      {generated && (
        <div className="generated">
          <div className="generated-label">URL</div>
          <div className="generated-url">{generated}</div>
          <button className="btn" onClick={copyToClipboard}>Copy</button>
        </div>
      )}
      {status && <div className="status">{status}</div>}
    </div>
  )
}

function NoGuestLinkState({ onClose, purpose }) {
  return (
    <div className="printable-unavailable-shell guest-link-unavailable-shell">
      <div
        className="printable-unavailable-card guest-link-unavailable-card"
        role="status"
        aria-labelledby="guest-link-unavailable-title"
      >
        <div className="printable-unavailable-icon guest-link-feature-icon" aria-hidden="true">
          <FaLink />
        </div>
        <h2 id="guest-link-unavailable-title">Guest Link Not Available</h2>
        <p>
          A Guest Link is not yet set up for this invitation.
          Please contact our customer service to request or enable this feature.
        </p>
        {purpose && <div className="dashboard-feature-purpose">{purpose}</div>}
        <a
          className="printable-unavailable-primary"
          href="mailto:logicatechnology.dev@gmail.com?subject=Guest Link Feature Request"
        >
          Contact Customer Service
        </a>
        {onClose && (
          <button className="printable-unavailable-secondary" onClick={onClose}>
            Back to Seat Checker
          </button>
        )}
      </div>
    </div>
  )
}
